'use client'

import React, { useRef, useEffect, useState } from 'react'
import Image from 'next/image'
import { SEND_ARROW_URL } from '@constants/contactUs.constants'
import styles from './QuickActions.module.scss'

interface ChatInputProps {
  placeholder?: string
  onSend?: (message: string) => void
}

const ChatInput = ({ placeholder = 'Ask something else...', onSend }: ChatInputProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [message, setMessage] = useState('')
  const [isFocused, setIsFocused] = useState(false)

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
  }

  // Auto-resize textarea based on content
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current
    if (textarea) {
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = '24px'
      const newHeight = Math.min(textarea.scrollHeight, 200) // Max height of 200px
      textarea.style.height = `${newHeight}px`
    }
  }

  useEffect(() => {
    adjustTextareaHeight()
  }, [message])

  // Adjust height on mount
  useEffect(() => {
    adjustTextareaHeight()
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value)
  }

  const handleSend = () => {
    if (message.trim()) {
      if (onSend) {
        onSend(message.trim())
      }
      setMessage('')
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto'
      }
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div className={`${styles.chatInputContainer} ${isFocused ? styles.focused : ''}`}>
      <textarea
        ref={textareaRef}
        name="chatInput"
        className={styles.textarea}
        placeholder={placeholder}
        value={message}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        rows={1}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
      <button
        type="button"
        className={styles.sendButton}
        onClick={handleSend}
        disabled={!message.trim()}
        aria-label="Send message">
        <Image src={SEND_ARROW_URL} alt="Send" width={20} height={20} className={styles.sendIcon} />
      </button>
    </div>
  )
}

export default ChatInput
